<div class="fleet-page">
  <div *ngIf="loadError" class="connection-alert" role="alert">
    <nz-alert nzType="error" nzShowIcon nzMessage="Fleet Hub could not connect" [nzDescription]="loadError"></nz-alert>
    <button nz-button nzType="primary" (click)="retryInitialLoad()">Retry</button>
  </div>
  <ng-container *ngIf="!isEditing; else editorTemplate">
    <header class="page-heading">
      <div>
        <h1>Fleet Maintenance</h1>
        <p class="page-description">Manage fleet pricing, participating branches, authorisation requirements and procedure documents.</p>
      </div>
      <button nz-button nzType="primary" nzSize="large" (click)="openCreateModal()">
        <span nz-icon nzType="plus" aria-hidden="true"></span>
        New fleet
      </button>
    </header>

    <section class="summary-grid" aria-label="Fleet summary">
      <article class="summary-card">
        <span class="summary-label">Total fleets</span>
        <strong>{{ fleets.length }}</strong>
        <small>Available for maintenance</small>
      </article>
      <article class="summary-card">
        <span class="summary-label">Visible</span>
        <strong class="color-success">{{ activeCount }}</strong>
        <small>Shown to participating branches</small>
      </article>
      <article class="summary-card">
        <span class="summary-label">Hidden</span>
        <strong>{{ hiddenCount }}</strong>
        <small>Retained and ready to restore</small>
      </article>
      <article class="summary-card">
        <span class="summary-label">Setup complete</span>
        <strong class="color-success">{{ completeSetupCount }}</strong>
        <small>All required information available</small>
      </article>
    </section>

    <section class="workspace-card">
      <div class="table-toolbar">
        <div>
          <h2>Fleet library</h2>
          <p>Select a fleet to review or maintain its complete configuration.</p>
        </div>
        <div class="search-actions">
          <nz-input-group nzPrefixIcon="search" class="search-control">
            <input
              nz-input
              aria-label="Search fleets"
              name="fleetSearch"
              autocomplete="off"
              [nzAutocomplete]="fleetNameSuggestions"
              placeholder="Search fleet name…"
              [(ngModel)]="listQuery"
              (keyup.enter)="loadFleets()">
          </nz-input-group>
          <button *ngIf="listQuery" nz-button nzType="text" type="button" aria-label="Clear fleet search" (click)="listQuery = ''; loadFleets()">Clear</button>
          <button nz-button (click)="loadFleets()">Search</button>
        </div>
      </div>

      <nz-autocomplete #fleetNameSuggestions>
        <nz-auto-option *ngFor="let fleet of fleetSuggestions" [nzValue]="fleet.name">
          <span translate="no">{{ fleet.name }}</span>
        </nz-auto-option>
      </nz-autocomplete>

      <div class="fleet-table-frame">
      <nz-table
        #fleetTable
        [nzData]="fleets"
        [nzLoading]="isLoading"
        [nzPageSize]="20"
        [nzNoResult]="fleetEmptyState"
        nzShowPagination
        [nzScroll]="{ x: '1060px' }">
        <thead>
          <tr>
            <th nzWidth="280px" [nzSortFn]="sortFleetName">Fleet</th>
            <th nzWidth="115px" [nzSortFn]="sortFleetVisibility">Visibility</th>
            <th nzWidth="135px" nzAlign="right" [nzSortFn]="sortFleetPricing">Pricing rules</th>
            <th nzWidth="155px" nzAlign="right" [nzSortFn]="sortFleetStockExceptions">Stock exceptions</th>
            <th nzWidth="140px" [nzSortFn]="sortFleetAvailability">Availability</th>
            <th nzWidth="165px" [nzSortFn]="sortFleetSetup">Setup status</th>
            <th nzWidth="100px" nzAlign="right">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let fleet of fleetTable.data">
            <td>
              <button class="fleet-name-button" type="button" translate="no" (click)="editFleet(fleet.specialPriceSetId)">
                {{ fleet.name }}
              </button>
              <small>{{ authTypeLabel(fleet.fleetAuthType, fleet.fleetAuthSubType) }}</small>
            </td>
            <td>
              <nz-tag [nzColor]="fleet.isVisible ? 'success' : 'default'">
                {{ fleet.isVisible ? 'Visible' : 'Hidden' }}
              </nz-tag>
            </td>
            <td nzAlign="right">{{ fleet.matrixRuleCount }}</td>
            <td nzAlign="right">{{ fleet.skuExceptionCount }}</td>
            <td>{{ fleetAvailabilityLabel(fleet) }}</td>
            <td>
              <nz-tag *ngIf="fleet.isComplete" nzColor="success">Complete</nz-tag>
              <span
                *ngIf="!fleet.isComplete"
                class="setup-state"
                tabindex="0"
                nz-tooltip
                nzTooltipPlacement="topLeft"
                [nzTooltipTitle]="setupStatusTooltip(fleet)"
                [attr.aria-label]="'Setup needs attention. ' + setupStatusTooltip(fleet)">
                <nz-tag nzColor="warning">Needs attention</nz-tag>
                <span class="setup-hint-icon" nz-icon nzType="info-circle" aria-hidden="true"></span>
              </span>
            </td>
            <td nzAlign="right">
              <button nz-button nzType="default" (click)="editFleet(fleet.specialPriceSetId)">
                Edit
                <span nz-icon nzType="right" aria-hidden="true"></span>
              </button>
            </td>
          </tr>
        </tbody>
      </nz-table>
      </div>
      <ng-template #fleetEmptyState>
        <div class="empty-table-state">
          <strong>No fleets found</strong>
          <span *ngIf="listQuery">Try another fleet name or clear the search.</span>
          <span *ngIf="!listQuery">Create a fleet to get started.</span>
        </div>
      </ng-template>
    </section>
  </ng-container>
</div>

<ng-template #editorTemplate>
  <ng-container *ngIf="configuration as fleet">
    <header class="editor-heading">
      <div class="editor-title">
        <button nz-button nzType="text" aria-label="Back to fleet library" (click)="backToList()">
          <span nz-icon nzType="arrow-left" aria-hidden="true"></span>
        </button>
        <div>
          <div class="title-line" aria-live="polite">
            <h1 translate="no">{{ fleet.specialPriceSetId ? fleet.overview.name : 'New fleet' }}</h1>
            <nz-tag [nzColor]="fleet.overview.isVisible ? 'success' : 'default'">
              {{ fleet.overview.isVisible ? 'Visible' : 'Hidden' }}
            </nz-tag>
            <nz-tag *ngIf="isDirty" nzColor="warning">Unsaved changes</nz-tag>
          </div>
          <p>Changes remain unsaved until you select Save fleet.</p>
        </div>
      </div>
      <div class="editor-actions">
        <button
          *ngIf="fleet.specialPriceSetId"
          nz-button
          nzSize="large"
          [nzDanger]="fleet.overview.isVisible"
          [nzLoading]="isUpdatingVisibility"
          [disabled]="isSaving"
          nz-popconfirm
          [nzPopconfirmTitle]="fleet.overview.isVisible ? 'Hide this fleet from branches?' : 'Restore this fleet for branches?'"
          nzPopconfirmPlacement="bottomRight"
          (nzOnConfirm)="toggleVisibility()">
          <span nz-icon [nzType]="fleet.overview.isVisible ? 'eye-invisible' : 'eye'" aria-hidden="true"></span>
          {{ fleet.overview.isVisible ? 'Hide fleet' : 'Restore fleet' }}
        </button>
        <button
          nz-button
          nzType="primary"
          nzSize="large"
          [nzLoading]="isSaving"
          [disabled]="isUpdatingVisibility"
          (click)="saveFleet()">
          <span nz-icon nzType="save" aria-hidden="true"></span>
          Save fleet
        </button>
      </div>
    </header>

    <nz-spin [nzSpinning]="isLoading || isSaving">
      <nz-alert
        *ngIf="saveError"
        class="save-error-alert"
        nzType="error"
        nzShowIcon
        nzMessage="Review the highlighted section before saving"
        [nzDescription]="saveError.message">
      </nz-alert>
      <section class="editor-card">
        <nz-tabset nzSize="large" [nzAnimated]="false" [(nzSelectedIndex)]="activeTabIndex">
          <nz-tab nzTitle="Overview">
            <div class="tab-panel narrow-panel">
              <div class="section-heading">
                <h2>Fleet details</h2>
                <p>The details branches see when selecting and authorising this fleet.</p>
              </div>
              <div class="form-grid">
                <label class="form-field full-width">
                  <span>Fleet name <em>*</em></span>
                  <input
                    nz-input
                    name="fleetName"
                    autocomplete="off"
                    maxlength="50"
                    class="uppercase-input"
                    [ngModel]="fleet.overview.name"
                    [attr.aria-invalid]="nameInvalid"
                    [attr.aria-describedby]="nameInvalid ? 'fleet-name-error' : null"
                    (ngModelChange)="setFleetName($event)"
                    placeholder="e.g. AVIS FLEET">
                  <small *ngIf="nameInvalid" id="fleet-name-error" class="form-error" role="alert">Enter a fleet name before saving.</small>
                </label>
                <label class="form-field">
                  <span>Call-centre number</span>
                  <input
                    nz-input
                    type="tel"
                    inputmode="tel"
                    name="callCentreNumber"
                    autocomplete="off"
                    maxlength="25"
                    [(ngModel)]="fleet.overview.callCentreNumber"
                    [attr.aria-invalid]="!!callCentreNumberError"
                    [attr.aria-describedby]="callCentreNumberError ? 'call-centre-number-error' : null"
                    (ngModelChange)="markDirty()"
                    placeholder="e.g. 011 555 0123">
                  <small *ngIf="callCentreNumberError" id="call-centre-number-error" class="form-error" role="alert">{{ callCentreNumberError }}</small>
                </label>
                <label class="form-field">
                  <span>Call-centre email</span>
                  <input
                    nz-input
                    type="email"
                    inputmode="email"
                    name="callCentreEmail"
                    autocomplete="off"
                    spellcheck="false"
                    maxlength="50"
                    [(ngModel)]="fleet.overview.callCentreEmail"
                    [attr.aria-invalid]="!!callCentreEmailError"
                    [attr.aria-describedby]="callCentreEmailError ? 'call-centre-email-error' : null"
                    (ngModelChange)="markDirty()"
                    placeholder="fleet@example.co.za">
                  <small *ngIf="callCentreEmailError" id="call-centre-email-error" class="form-error" role="alert">{{ callCentreEmailError }}</small>
                </label>
                <div class="form-field">
                  <span>Fleet service package</span>
                  <nz-select nzAllowClear nzShowSearch aria-label="Fleet service package" [(ngModel)]="fleet.overview.customStockGroupId" (ngModelChange)="markDirty()" nzPlaceHolder="No fleet service package">
                    <nz-option *ngFor="let group of lookups.customStockGroups" [nzValue]="group.id" [nzLabel]="group.name"></nz-option>
                  </nz-select>
                  <small>Values: alignment, balancing, then valve.</small>
                </div>
                <div class="form-field dynamic-pricing-field">
                  <label for="dynamic-pricing-switch">Dynamic pricing</label>
                  <div class="toggle-setting">
                    <div class="toggle-control">
                      <nz-switch
                        nzId="dynamic-pricing-switch"
                        [(ngModel)]="fleet.overview.hasDynamicPricing"
                        (ngModelChange)="markDirty()"
                        nzCheckedChildren="On"
                        nzUnCheckedChildren="Off">
                      </nz-switch>
                    </div>
                    <small>Enable or disable dynamic fleet pricing for this fleet.</small>
                  </div>
                </div>
                <div class="form-field full-width">
                  <span>Authorisation mode</span>
                  <ng-container *ngIf="!isProtectedAuthType; else protectedAuthType">
                    <nz-radio-group
                      class="auth-mode-picker"
                      aria-label="Authorisation mode"
                      [attr.aria-invalid]="!!authConfigurationError"
                      [attr.aria-describedby]="authConfigurationError ? 'auth-configuration-error' : null"
                      [ngModel]="selectedAuthMode"
                      (ngModelChange)="setAuthMode($event)">
                      <label nz-radio nzValue="eauth">
                        <span class="choice-copy">
                          <strong>eAuth</strong>
                          <small>Electronic authorisation with a required manual or email fallback.</small>
                        </span>
                      </label>
                      <label nz-radio nzValue="manual">
                        <span class="choice-copy">
                          <strong>Manual Auth</strong>
                          <small>The branch completes authorisation manually using the configured fields.</small>
                        </span>
                      </label>
                      <label nz-radio nzValue="email">
                        <span class="choice-copy">
                          <strong>Email Auth</strong>
                          <small>The branch sends the authorisation request by email using the configured fields.</small>
                        </span>
                      </label>
                    </nz-radio-group>
                    <div *ngIf="selectedAuthMode === 'eauth'" class="eauth-fallback-panel">
                      <div>
                        <strong>eAuth fallback <em>*</em></strong>
                        <small>Choose how authorisation must continue if eAuth cannot complete.</small>
                      </div>
                      <nz-radio-group
                        aria-label="eAuth fallback method"
                        aria-required="true"
                        [attr.aria-invalid]="!!authConfigurationError"
                        [attr.aria-describedby]="authConfigurationError ? 'auth-configuration-error' : null"
                        [ngModel]="fleet.overview.fleetAuthSubType"
                        (ngModelChange)="setEAuthFallback($event)">
                        <label nz-radio [nzValue]="1">Manual Auth</label>
                        <label nz-radio [nzValue]="2">Email Auth</label>
                      </nz-radio-group>
                    </div>
                    <small *ngIf="authConfigurationError" id="auth-configuration-error" class="form-error auth-mode-error" role="alert">
                      {{ authConfigurationError }}
                      <span *ngIf="fleet.specialPriceSetId && !authConfigurationWasChanged"> You can still save unrelated sections without changing this legacy setup.</span>
                    </small>
                  </ng-container>
                  <ng-template #protectedAuthType>
                    <input nz-input aria-label="Protected authorisation mode" [ngModel]="authTypeLabel(fleet.overview.fleetAuthType, fleet.overview.fleetAuthSubType)" readonly>
                    <small>This authorisation method is managed by StockFinder and will not be changed.</small>
                  </ng-template>
                </div>
              </div>
            </div>
          </nz-tab>

          <nz-tab [nzTitle]="pricingTitle">
            <ng-template #pricingTitle>
              <span class="tab-title">Pricing rules <span class="tab-count">{{ fleet.matrixRules.length }}</span></span>
            </ng-template>
            <div class="tab-panel">
              <div class="section-heading split-heading">
                <div>
                  <h2>Brand and rim pricing</h2>
                  <p>Set fleet pricing by brand, optional rim and tyre class. A rim-specific rule takes priority over the generic brand rule for the same class. Tyres without a matching rule are not sold.</p>
                </div>
                <button nz-button nzDanger [disabled]="!hasSelectedMatrixRules" (click)="removeSelectedMatrixRules()">
                  <span nz-icon nzType="delete" aria-hidden="true"></span>
                  Remove selected<span *ngIf="selectedMatrixRuleCount"> ({{ selectedMatrixRuleCount }})</span>
                </button>
              </div>

              <nz-alert
                *ngIf="pricingRuleIssues.length"
                nzType="warning"
                nzShowIcon
                nzMessage="Pricing rules need attention"
                [nzDescription]="pricingRuleIssues.join(' ')">
              </nz-alert>

              <div class="add-strip matrix-add-strip labelled-add-strip">
                <div class="add-field grow-field">
                  <span>Brands</span>
                  <nz-select
                  class="grow-control"
                  nzMode="multiple"
                  nzShowSearch
                  [nzServerSearch]="true"
                  [nzLoading]="isSearchingBrands"
                  aria-label="Brands to add"
                  [(ngModel)]="newBrandIds"
                  (nzOnSearch)="searchBrands($event)"
                  (nzOpenChange)="$event && searchBrands('')"
                  nzNotFoundContent="No matching brands"
                  nzPlaceHolder="Start typing a brand name…">
                    <nz-option *ngFor="let brand of brandOptions" [nzValue]="brand.brandId" [nzLabel]="brand.name"></nz-option>
                  </nz-select>
                </div>
                <div class="add-field compact-add-field rim-add-field">
                  <span>Rim override</span>
                  <nz-select
                    nzShowSearch
                    [ngModel]="newMatrixRim"
                    (ngModelChange)="setNewMatrixRim($event)"
                    aria-label="Rim override for new pricing rules"
                    nzNotFoundContent="No matching rim size"
                    nzPlaceHolder="All rims">
                    <nz-option [nzValue]="null" nzLabel="All rims"></nz-option>
                    <nz-option *ngFor="let rim of lookups.rimSizes" [nzValue]="rim" [nzLabel]="rimOptionLabel(rim)"></nz-option>
                  </nz-select>
                </div>
                <div class="add-field">
                  <span>Tyre class</span>
                  <nz-select [(ngModel)]="newMatrixCommercial" aria-label="Commercial classification for new pricing rules">
                    <nz-option [nzValue]="false" nzLabel="Non-commercial"></nz-option>
                    <nz-option [nzValue]="true" nzLabel="Commercial"></nz-option>
                  </nz-select>
                </div>
                <div class="add-field">
                  <span>Pricing method</span>
                  <nz-select [(ngModel)]="newMatrixMethodId" (ngModelChange)="setNewMatrixMethod($event)" aria-label="New pricing method">
                    <nz-option *ngFor="let method of matrixPriceMethods" [nzValue]="method.id" [nzLabel]="method.name"></nz-option>
                  </nz-select>
                </div>
                <div class="add-field compact-add-field">
                  <span>Value</span>
                  <nz-input-number [(ngModel)]="newMatrixValue" [nzMin]="0" [nzMax]="pricingValueMax(newMatrixMethodId)" [nzStep]="0.01" [nzPrecision]="2" [nzDisabled]="newMatrixMethodId === 3" aria-label="New pricing value"></nz-input-number>
                  <small *ngIf="newMatrixValueError" class="field-error" role="alert">{{ newMatrixValueError }}</small>
                </div>
                <button nz-button nzType="primary" [disabled]="newBrandIds.length === 0 || !!newMatrixValueError" (click)="addMatrixRules()">
                  Add rules
                </button>
              </div>

              <div class="table-filter">
                <nz-input-group nzPrefixIcon="search">
                  <input nz-input name="matrixFilter" autocomplete="off" [(ngModel)]="matrixQuery" [nzAutocomplete]="matrixFilterAutocomplete" placeholder="Filter brand, rim, class or method…" aria-label="Filter pricing rules">
                </nz-input-group>
                <button *ngIf="matrixQuery" nz-button nzType="text" type="button" aria-label="Clear pricing rule filter" (click)="matrixQuery = ''">Clear</button>
                <span class="result-count">{{ filteredMatrixRules.length }} of {{ fleet.matrixRules.length }}</span>
              </div>
              <nz-autocomplete #matrixFilterAutocomplete>
                <nz-auto-option *ngFor="let suggestion of matrixFilterSuggestions" [nzValue]="suggestion">{{ suggestion }}</nz-auto-option>
              </nz-autocomplete>
              <nz-table #matrixTable [nzData]="filteredMatrixRules" [nzPageSize]="20" [nzNoResult]="matrixEmptyState" [nzScroll]="{ x: '980px' }">
                <thead>
                  <tr>
                    <th nzWidth="52px"></th>
                    <th [nzSortFn]="sortMatrixBrand">Brand</th>
                    <th nzWidth="170px" [nzSortFn]="sortMatrixRim">Rim</th>
                    <th nzWidth="170px" [nzSortFn]="sortMatrixClassification">Classification</th>
                    <th nzWidth="190px" [nzSortFn]="sortMatrixMethod">Method</th>
                    <th nzWidth="180px" [nzSortFn]="sortMatrixValue">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let rule of matrixTable.data" [class.has-validation-issue]="matrixRuleHasIssue(rule)">
                    <td><label nz-checkbox [(ngModel)]="rule.selected" [attr.aria-label]="'Select pricing rule for ' + rule.brandName"></label></td>
                    <td>
                      <strong translate="no">{{ rule.brandName || ('Brand ' + rule.brandId) }}</strong>
                      <small *ngIf="matrixRuleIssueMessage(rule) as issue" class="row-error" role="alert">{{ issue }}</small>
                    </td>
                    <td>
                      <nz-select
                        class="rim-rule-select"
                        nzShowSearch
                        [ngModel]="rule.rim"
                        (ngModelChange)="setMatrixRuleRim(rule, $event)"
                        [attr.aria-label]="'Rim override for ' + rule.brandName"
                        nzNotFoundContent="No matching rim size"
                        nzPlaceHolder="All rims">
                        <nz-option [nzValue]="null" nzLabel="All rims"></nz-option>
                        <nz-option
                          *ngIf="isUnavailableRim(rule.rim)"
                          [nzValue]="rule.rim"
                          [nzLabel]="existingRimOptionLabel(rule.rim!)"
                          [nzDisabled]="true">
                        </nz-option>
                        <nz-option *ngFor="let rim of lookups.rimSizes" [nzValue]="rim" [nzLabel]="rimOptionLabel(rim)"></nz-option>
                      </nz-select>
                    </td>
                    <td>
                      <nz-select [(ngModel)]="rule.isCommercial" (ngModelChange)="markDirty()" [attr.aria-label]="'Commercial classification for ' + rule.brandName">
                        <nz-option [nzValue]="false" nzLabel="Non-commercial"></nz-option>
                        <nz-option [nzValue]="true" nzLabel="Commercial"></nz-option>
                      </nz-select>
                    </td>
                    <td>
                      <nz-select [(ngModel)]="rule.priceMatrixMethodId" (ngModelChange)="setMatrixMethod(rule, $event)" [attr.aria-label]="'Pricing method for ' + rule.brandName">
                        <nz-option
                          *ngIf="isLegacyMatrixDontSell(rule)"
                          [nzValue]="3"
                          nzLabel="Existing: Don't Sell"
                          [nzDisabled]="true">
                        </nz-option>
                        <nz-option *ngFor="let method of matrixPriceMethods" [nzValue]="method.id" [nzLabel]="method.name"></nz-option>
                      </nz-select>
                    </td>
                    <td nzAlign="right">
                      <nz-input-number [(ngModel)]="rule.value" (ngModelChange)="markDirty()" [nzMin]="0" [nzMax]="pricingValueMax(rule.priceMatrixMethodId)" [nzDisabled]="rule.priceMatrixMethodId === 3" [nzStep]="0.01" [nzPrecision]="2" [attr.aria-label]="'Pricing value for ' + rule.brandName"></nz-input-number>
                    </td>
                  </tr>
                </tbody>
              </nz-table>
              <ng-template #matrixEmptyState>
                <div class="empty-table-state">
                  <strong>{{ matrixQuery ? 'No matching pricing rules' : 'No brand pricing rules yet' }}</strong>
                  <span>{{ matrixQuery ? 'Choose another suggestion or clear the filter.' : 'Search for a brand above to add the first rule.' }}</span>
                </div>
              </ng-template>
            </div>
          </nz-tab>

          <nz-tab [nzTitle]="skuTitle">
            <ng-template #skuTitle>
              <span class="tab-title">Stock code exceptions <span class="tab-count">{{ fleet.skuExceptions.length }}</span></span>
            </ng-template>
            <div class="tab-panel">
              <div class="section-heading split-heading">
                <div>
                  <h2>Stock code price exceptions</h2>
                  <p>Set an exception for a specific stock code using either a fixed price excluding VAT or a fleet pricing method. Stock code exceptions take priority over Pricing Rules. Use Don't Sell to block a tyre that would otherwise match a pricing rule.</p>
                </div>
                <button nz-button nzDanger [disabled]="!hasSelectedSkuExceptions" (click)="removeSelectedSkuExceptions()">
                  <span nz-icon nzType="delete" aria-hidden="true"></span>
                  Remove selected<span *ngIf="selectedSkuExceptionCount"> ({{ selectedSkuExceptionCount }})</span>
                </button>
              </div>
              <nz-alert
                *ngIf="stockCodeExceptionIssues.length"
                nzType="warning"
                nzShowIcon
                nzMessage="Stock code exceptions need attention"
                [nzDescription]="stockCodeExceptionIssues.join(' ')">
              </nz-alert>
              <div class="add-strip labelled-add-strip sku-add-strip">
                <div class="add-field grow-field">
                  <span>Stock codes</span>
                  <nz-select
                    class="grow-control"
                  nzMode="multiple"
                  nzShowSearch
                  [nzServerSearch]="true"
                  [nzLoading]="isSearchingTyres"
                  aria-label="Stock code exceptions to add"
                  [(ngModel)]="newSkuIds"
                  (nzOnSearch)="searchTyres($event)"
                  (nzOpenChange)="$event && searchTyres('')"
                    nzNotFoundContent="No matching stock codes"
                    nzPlaceHolder="Start typing a stock code, description, brand or rim…">
                    <nz-option
                      *ngFor="let tyre of tyreOptions"
                      [nzValue]="tyre.msfid"
                      [nzLabel]="tyre.stockCode + ' · ' + tyre.description">
                    </nz-option>
                  </nz-select>
                </div>
                <div class="add-field compact-add-field">
                  <span>Price type</span>
                  <nz-select
                    [ngModel]="newSkuMode"
                    (ngModelChange)="setNewSkuMode($event)"
                    aria-label="New stock code exception price type">
                    <nz-option nzValue="method" nzLabel="Pricing method"></nz-option>
                    <nz-option nzValue="fixed" nzLabel="Fixed price"></nz-option>
                  </nz-select>
                </div>
                <div *ngIf="newSkuMode === 'method'" class="add-field compact-add-field">
                  <span>Pricing method</span>
                  <nz-select
                    [ngModel]="newSkuMethodId"
                    (ngModelChange)="setNewSkuMethod($event)"
                    aria-label="New stock code exception pricing method">
                    <nz-option *ngFor="let method of lookups.priceMethods" [nzValue]="method.id" [nzLabel]="method.name"></nz-option>
                  </nz-select>
                </div>
                <div class="add-field compact-add-field sku-value-field">
                  <span>{{ newSkuMode === 'fixed' ? 'Fixed price (excl. VAT)' : 'Value' }}</span>
                  <div *ngIf="newSkuMode === 'fixed'" class="money-input add-money-input">
                    <span aria-hidden="true">R</span>
                    <nz-input-number
                      [(ngModel)]="newSkuFixedPrice"
                      [nzMin]="0.01"
                      [nzStep]="1"
                      [nzPrecision]="2"
                      aria-label="New fixed stock code price excluding VAT">
                    </nz-input-number>
                  </div>
                  <nz-input-number
                    *ngIf="newSkuMode === 'method'"
                    [(ngModel)]="newSkuMethodValue"
                    [nzMin]="0"
                    [nzMax]="pricingValueMax(newSkuMethodId)"
                    [nzDisabled]="newSkuMethodId === 3"
                    [nzStep]="0.01"
                    [nzPrecision]="2"
                    aria-label="New stock code exception pricing value">
                  </nz-input-number>
                  <small *ngIf="newSkuExceptionValueError" class="field-error" role="alert">{{ newSkuExceptionValueError }}</small>
                </div>
                <button
                  nz-button
                  nzType="primary"
                  [disabled]="newSkuIds.length === 0 || !!newSkuExceptionValueError"
                  (click)="addSkuExceptions()">
                  Add exceptions
                </button>
              </div>
              <div class="table-filter">
                <nz-input-group nzPrefixIcon="search">
                  <input nz-input name="skuFilter" autocomplete="off" [(ngModel)]="skuQuery" [nzAutocomplete]="skuFilterAutocomplete" placeholder="Filter stock code or description…" aria-label="Filter stock code exceptions">
                </nz-input-group>
                <button *ngIf="skuQuery" nz-button nzType="text" type="button" aria-label="Clear stock code filter" (click)="skuQuery = ''">Clear</button>
                <span class="result-count">{{ filteredSkuExceptions.length }} of {{ fleet.skuExceptions.length }}</span>
              </div>
              <nz-autocomplete #skuFilterAutocomplete>
                <nz-auto-option *ngFor="let suggestion of skuFilterSuggestions" [nzValue]="suggestion">{{ suggestion }}</nz-auto-option>
              </nz-autocomplete>
              <nz-table #skuTable [nzData]="filteredSkuExceptions" [nzPageSize]="20" [(nzPageIndex)]="skuTablePageIndex" [nzNoResult]="stockEmptyState" [nzScroll]="{ x: '1050px' }">
                <thead>
                  <tr>
                    <th nzWidth="52px"></th>
                    <th nzWidth="150px" [nzSortFn]="sortStockCode">Stock code</th>
                    <th [nzSortFn]="sortStockDescription">Description</th>
                    <th nzWidth="160px" [nzSortFn]="sortStockPriceType">Price type</th>
                    <th nzWidth="180px" [nzSortFn]="sortStockMethod">Method</th>
                    <th nzWidth="160px" [nzSortFn]="sortStockValue">Value</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let item of skuTable.data" [class.has-validation-issue]="skuExceptionHasIssue(item)">
                    <td><label nz-checkbox [(ngModel)]="item.selected" [attr.aria-label]="'Select stock code exception ' + item.stockCode"></label></td>
                    <td translate="no"><strong>{{ item.stockCode }}</strong></td>
                    <td>
                      {{ item.description }}
                      <small *ngIf="skuExceptionIssueMessage(item) as issue" class="row-error" role="alert">{{ issue }}</small>
                    </td>
                    <td>
                      <nz-select [ngModel]="skuMode(item)" (ngModelChange)="setSkuMode(item, $event)" [attr.aria-label]="'Price type for ' + item.stockCode">
                        <nz-option nzValue="fixed" nzLabel="Fixed price (excl. VAT)"></nz-option>
                        <nz-option nzValue="method" nzLabel="Pricing method"></nz-option>
                      </nz-select>
                    </td>
                    <td>
                      <nz-select
                        *ngIf="skuMode(item) === 'method'"
                        [(ngModel)]="item.priceMatrixMethodId"
                        [attr.aria-label]="'Pricing method for ' + item.stockCode"
                        (ngModelChange)="setSkuMethod(item, $event)">
                        <nz-option *ngFor="let method of lookups.priceMethods" [nzValue]="method.id" [nzLabel]="method.name"></nz-option>
                      </nz-select>
                      <span *ngIf="skuMode(item) === 'fixed'" class="muted-text">—</span>
                    </td>
                    <td nzAlign="right">
                      <div *ngIf="skuMode(item) === 'fixed'" class="money-field">
                        <div class="money-input">
                          <span aria-hidden="true">R</span>
                          <nz-input-number
                            [(ngModel)]="item.price"
                            (ngModelChange)="markDirty()"
                            [nzMin]="0.01"
                            [nzStep]="1"
                            [nzPrecision]="2"
                            [attr.aria-label]="'Fixed price excluding VAT for ' + item.stockCode"
                            nzPlaceHolder="0.00">
                          </nz-input-number>
                        </div>
                        <small>Excl. VAT</small>
                      </div>
                      <nz-input-number
                        *ngIf="skuMode(item) === 'method'"
                        [(ngModel)]="item.priceMatrixMethodValue"
                        (ngModelChange)="markDirty()"
                        [nzMin]="0"
                        [nzMax]="pricingValueMax(item.priceMatrixMethodId)"
                        [nzDisabled]="item.priceMatrixMethodId === 3"
                        [nzStep]="0.01"
                        [nzPrecision]="2"
                        [attr.aria-label]="'Pricing value for ' + item.stockCode">
                      </nz-input-number>
                    </td>
                  </tr>
                </tbody>
              </nz-table>
              <ng-template #stockEmptyState>
                <div class="empty-table-state">
                  <strong>{{ skuQuery ? 'No matching stock code exceptions' : 'No stock code exceptions yet' }}</strong>
                  <span>{{ skuQuery ? 'Choose another suggestion or clear the filter.' : 'Search for a stock code above to add an exception.' }}</span>
                </div>
              </ng-template>
            </div>
          </nz-tab>

          <nz-tab [nzTitle]="availabilityTitle">
            <ng-template #availabilityTitle>
              <span class="tab-title">Availability <span class="tab-count">{{ availabilityCount }}</span></span>
            </ng-template>
            <div class="tab-panel">
              <div class="section-heading">
                <h2>Choose where the fleet is available</h2>
                <p>Make the fleet available to entire franchises or to selected branches. These options cannot be combined.</p>
              </div>

              <nz-radio-group
                class="availability-mode-options"
                aria-label="Fleet availability method"
                [ngModel]="availabilityMode"
                (ngModelChange)="setAvailabilityMode($event)">
                <label nz-radio nzValue="franchises">
                  <span class="choice-copy">
                    <strong>Entire franchises</strong>
                    <small>Every active Tyremart or Tyreworx branch in the selected franchise can use the fleet.</small>
                  </span>
                </label>
                <label nz-radio nzValue="branches">
                  <span class="choice-copy">
                    <strong>Specific branches</strong>
                    <small>Choose individual active Tyremart or Tyreworx branches and maintain their merchant numbers.</small>
                  </span>
                </label>
              </nz-radio-group>

              <div *ngIf="availabilityMode === 'franchises'" class="franchise-picker availability-config-panel">
                <div>
                  <strong>Select entire franchises</strong>
                  <small>Only Tyremart and Tyreworx franchises are available.</small>
                </div>
                <div class="franchise-options">
                  <label
                    *ngFor="let franchise of lookups.franchises"
                    nz-checkbox
                    class="franchise-option"
                    [class.franchise-option-selected]="fleet.franchiseIds.includes(franchise.id)"
                    [ngModel]="fleet.franchiseIds.includes(franchise.id)"
                    (ngModelChange)="toggleFranchise(franchise.id, $event)">
                    <span class="franchise-option-name" translate="no">{{ franchise.name }}</span>
                  </label>
                </div>
                <span *ngIf="fleet.franchiseIds.length === 0" class="availability-inline-error" role="alert">Select at least one Tyremart or Tyreworx franchise.</span>
              </div>
              <nz-alert
                *ngIf="!availabilityMode"
                nzType="warning"
                nzShowIcon
                nzMessage="Choose an availability method"
                [nzDescription]="hasConflictingAvailability
                  ? 'This fleet currently contains both franchise and branch mappings. Choose one method to correct it when you save.'
                  : 'Select whether this fleet applies to entire franchises or selected branches.'">
              </nz-alert>

              <nz-alert
                *ngIf="availabilityIssues.length && availabilityMode"
                class="availability-issues"
                nzType="warning"
                nzShowIcon
                nzMessage="Existing availability needs attention"
                [nzDescription]="availabilityIssues.join(' ')">
              </nz-alert>

              <ng-container *ngIf="availabilityMode === 'branches'">
              <div class="section-heading compact-heading split-heading availability-config-panel">
                <div>
                  <h3>Specific participating branches</h3>
                  <p>Add active Tyremart or Tyreworx branches and maintain a merchant number for each one.</p>
                </div>
                <button nz-button nzDanger [disabled]="!hasSelectedSellers" (click)="removeSelectedSellers()">
                  <span nz-icon nzType="delete" aria-hidden="true"></span>
                  Remove selected<span *ngIf="selectedSellerCount"> ({{ selectedSellerCount }})</span>
                </button>
              </div>
              <div class="add-strip labelled-add-strip">
                <div class="add-field grow-field">
                  <span>Participating branches</span>
                  <nz-select
                    class="grow-control"
                  nzMode="multiple"
                  nzShowSearch
                  [nzServerSearch]="true"
                  [nzLoading]="isSearchingSellers"
                  aria-label="Branches to add"
                  [(ngModel)]="newSellerIds"
                  (nzOnSearch)="searchSellers($event)"
                  (nzOpenChange)="$event && searchSellers('')"
                    nzNotFoundContent="No matching active Tyremart or Tyreworx branches"
                    nzPlaceHolder="Start typing a branch name, city or province…">
                    <nz-option
                      *ngFor="let seller of sellerOptions"
                      [nzValue]="seller.sellerId"
                      [nzLabel]="seller.name + (seller.city ? ' · ' + seller.city : '')">
                    </nz-option>
                  </nz-select>
                </div>
                <button nz-button nzType="primary" [disabled]="newSellerIds.length === 0" (click)="addSellers()">Add branches</button>
              </div>

              <span *ngIf="fleet.sellers.length === 0" class="availability-inline-error" role="alert">Add at least one active Tyremart or Tyreworx branch.</span>

              <div class="merchant-panel">
                <div class="merchant-panel-heading">
                  <div>
                    <strong>Merchant number handling</strong>
                    <small>Use one shared merchant number for every selected branch, or maintain a different number for each branch.</small>
                  </div>
                  <nz-radio-group [ngModel]="merchantMode" (ngModelChange)="setMerchantMode($event)" aria-label="Merchant number handling">
                    <label nz-radio-button nzValue="shared">One shared number</label>
                    <label nz-radio-button nzValue="perBranch">Different per branch</label>
                  </nz-radio-group>
                </div>
                <label *ngIf="merchantMode === 'shared'" class="form-field merchant-shared-field">
                  <span>Shared merchant number</span>
                  <input
                    nz-input
                    autocomplete="off"
                    spellcheck="false"
                    maxlength="50"
                    name="sharedMerchantNumber"
                    [(ngModel)]="sharedMerchantNumber"
                    [nzAutocomplete]="merchantSuggestions"
                    (focus)="searchMerchantNumbers(sharedMerchantNumber || '')"
                    (ngModelChange)="searchMerchantNumbers($event || ''); markDirty()"
                    placeholder="Type or choose an existing number…">
                </label>
              </div>

              <nz-autocomplete #merchantSuggestions>
                <nz-auto-option *ngFor="let merchantNumber of merchantNumberOptions" [nzValue]="merchantNumber">
                  {{ merchantNumber }}
                </nz-auto-option>
              </nz-autocomplete>

              <div class="table-filter">
                <nz-input-group nzPrefixIcon="search">
                  <input nz-input name="sellerFilter" autocomplete="off" [(ngModel)]="sellerQuery" [nzAutocomplete]="sellerFilterAutocomplete" placeholder="Filter branch, franchise or merchant number…" aria-label="Filter participating branches">
                </nz-input-group>
                <button *ngIf="sellerQuery" nz-button nzType="text" type="button" aria-label="Clear participating branch filter" (click)="sellerQuery = ''">Clear</button>
                <span class="result-count">{{ filteredSellers.length }} of {{ fleet.sellers.length }}</span>
              </div>
              <nz-autocomplete #sellerFilterAutocomplete>
                <nz-auto-option *ngFor="let suggestion of sellerFilterSuggestions" [nzValue]="suggestion">{{ suggestion }}</nz-auto-option>
              </nz-autocomplete>
              <nz-table #sellerTable [nzData]="filteredSellers" [nzPageSize]="20" [(nzPageIndex)]="sellerTablePageIndex" [nzNoResult]="sellerEmptyState" [nzScroll]="{ x: '900px' }">
                <thead>
                  <tr>
                    <th nzWidth="52px"></th>
                    <th [nzSortFn]="sortSellerName">Branch</th>
                    <th nzWidth="180px" [nzSortFn]="sortSellerFranchise">Franchise</th>
                    <th nzWidth="340px" [nzSortFn]="sortSellerMerchant">Merchant number</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let seller of sellerTable.data">
                    <td><label nz-checkbox [(ngModel)]="seller.selected" [attr.aria-label]="'Select branch ' + seller.sellerName"></label></td>
                    <td>
                      <strong translate="no">{{ seller.sellerName || 'Unavailable branch' }}</strong>
                      <nz-tag *ngIf="!seller.isSupported" nzColor="warning">Existing mapping</nz-tag>
                      <small *ngIf="!seller.isActive">Inactive branch</small>
                    </td>
                      <td translate="no">{{ franchiseName(seller.franchiseId) }}</td>
                    <td>
                      <span *ngIf="merchantMode === 'shared'" class="shared-merchant-value">
                        {{ sharedMerchantNumber || 'No shared number' }}
                      </span>
                      <input
                        *ngIf="merchantMode === 'perBranch'"
                        nz-input
                        autocomplete="off"
                        spellcheck="false"
                        maxlength="50"
                        [name]="'merchantNumber-' + seller.sellerId"
                        [(ngModel)]="seller.merchantNumber"
                        [nzAutocomplete]="merchantSuggestions"
                        (focus)="searchMerchantNumbers(seller.merchantNumber || '')"
                        (ngModelChange)="searchMerchantNumbers($event || ''); markDirty()"
                        [attr.aria-label]="'Merchant number for ' + seller.sellerName"
                        placeholder="Type or choose a merchant number…">
                    </td>
                  </tr>
                </tbody>
              </nz-table>
              <ng-template #sellerEmptyState>
                <div class="empty-table-state">
                  <strong>{{ sellerQuery ? 'No matching participating branches' : 'No specific branches selected' }}</strong>
                  <span>{{ sellerQuery ? 'Choose another suggestion or clear the filter.' : 'Use the branch search above to add participating branches.' }}</span>
                </div>
              </ng-template>
              </ng-container>
            </div>
          </nz-tab>

          <nz-tab [nzTitle]="authTitle">
            <ng-template #authTitle>
              <span class="tab-title">Authorisation fields <span class="tab-count">{{ fleet.authFields.length }}</span></span>
            </ng-template>
            <div class="tab-panel">
              <div class="section-heading split-heading">
                <div>
                  <h2>Required fleet information</h2>
                  <p>Control which details the branch must provide before sending Priority Path authorisation.</p>
                </div>
                <button *ngIf="fleet.overview.fleetAuthType === 2" nz-button type="button" (click)="openNewFieldModal()">
                  <span nz-icon nzType="plus" aria-hidden="true"></span>
                  Create new field
                </button>
              </div>

              <nz-alert
                *ngIf="fleet.overview.fleetAuthType !== 2"
                nzType="info"
                nzShowIcon
                nzMessage="Authorisation fields are managed by StockFinder"
                nzDescription="This fleet uses electronic authorisation, so its field setup cannot be changed here.">
              </nz-alert>

              <ng-container *ngIf="fleet.overview.fleetAuthType === 2">
                <div class="add-strip labelled-add-strip">
                  <div class="add-field grow-field">
                    <span>Existing field</span>
                    <nz-select class="grow-control" nzShowSearch [(ngModel)]="authFieldToAdd" aria-label="Existing authorisation field to add" nzPlaceHolder="Start typing or select a field…">
                      <nz-option
                        *ngFor="let field of authFieldsAvailable"
                        [nzValue]="field.fleetAuthFieldId"
                        [nzLabel]="field.fleetAuthFieldName">
                      </nz-option>
                    </nz-select>
                  </div>
                  <button nz-button nzType="primary" type="button" [disabled]="!authFieldToAdd" (click)="addExistingAuthField()">Add selected field</button>
                </div>
              </ng-container>

              <nz-table #authTable [nzData]="fleet.authFields" [nzPageSize]="20" [(nzPageIndex)]="authTablePageIndex" [nzScroll]="{ x: '800px' }">
                <thead>
                  <tr>
                    <th [nzSortFn]="sortAuthField">Field</th>
                    <th [nzSortFn]="sortAuthType">Type</th>
                    <th>Guidance</th>
                    <th nzWidth="120px" [nzSortFn]="sortAuthRequired">Required</th>
                    <th *ngIf="fleet.overview.fleetAuthType === 2" nzWidth="112px" nzAlign="right"><span class="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let field of authTable.data">
                    <td>
                      <strong>{{ field.fleetAuthFieldName }}</strong>
                      <nz-tag *ngIf="field.isNew" nzColor="blue">New</nz-tag>
                      <small *ngIf="field.modelField">Managed by StockFinder</small>
                    </td>
                    <td>{{ authDataTypeLabel(field.dataType) }}</td>
                    <td>{{ field.tooltipText || '—' }}</td>
                    <td>
                      <nz-switch
                        [(ngModel)]="field.isRequired"
                        (ngModelChange)="markDirty()"
                        [attr.aria-label]="'Required: ' + field.fleetAuthFieldName"
                        [nzDisabled]="fleet.overview.fleetAuthType !== 2">
                      </nz-switch>
                    </td>
                    <td *ngIf="fleet.overview.fleetAuthType === 2" nzAlign="right">
                      <div class="table-row-actions">
                        <button
                          *ngIf="field.fleetAuthFieldId !== null && !field.isNew"
                          nz-button
                          nzType="text"
                          [attr.aria-label]="'Edit ' + field.fleetAuthFieldName + ' everywhere'"
                          nz-tooltip
                          nzTooltipTitle="Edit this field everywhere"
                          (click)="openEditAuthField(field)">
                          <span nz-icon nzType="edit" aria-hidden="true"></span>
                        </button>
                        <button nz-button nzType="text" nzDanger [attr.aria-label]="'Remove ' + field.fleetAuthFieldName + ' from this fleet'" nz-tooltip nzTooltipTitle="Remove from this fleet" (click)="removeAuthField(field)">
                          <span nz-icon nzType="delete" aria-hidden="true"></span>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </nz-table>
            </div>
          </nz-tab>

          <nz-tab nzTitle="Procedure document">
            <div class="tab-panel narrow-panel">
              <div class="section-heading">
                <h2>Procedure document</h2>
                <p>This PDF opens when a branch selects the fleet during quoting.</p>
              </div>
              <div class="document-panel" [class.has-document]="!!fleet.documentBase64">
                <div class="document-icon"><span nz-icon nzType="file-pdf" aria-hidden="true"></span></div>
                <div class="document-details">
                  <strong>{{ fleet.documentBase64 ? documentLabel : 'No procedure document uploaded' }}</strong>
                  <span>{{ fleet.documentBase64 ? 'PDF ready for preview and save' : 'Upload a PDF up to 10 MiB.' }}</span>
                </div>
                <div class="document-actions">
                  <button *ngIf="fleet.documentBase64" nz-button (click)="previewDocument()">
                    <span nz-icon nzType="eye" aria-hidden="true"></span> Preview
                  </button>
                  <nz-upload [nzBeforeUpload]="beforeDocumentUpload" [nzShowUploadList]="false" nzAccept="application/pdf,.pdf">
                    <button nz-button nzType="primary">
                      <span nz-icon nzType="upload" aria-hidden="true"></span>
                      {{ fleet.documentBase64 ? 'Replace PDF' : 'Upload PDF' }}
                    </button>
                  </nz-upload>
                  <button *ngIf="fleet.documentBase64" nz-button nzDanger nz-popconfirm nzPopconfirmTitle="Remove this procedure document?" (nzOnConfirm)="removeDocument()">
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </nz-tab>
        </nz-tabset>
      </section>
    </nz-spin>
  </ng-container>
</ng-template>

<nz-modal
  [(nzVisible)]="createModalVisible"
  nzTitle="Create fleet"
  nzOkText="Open editor"
  [nzOkLoading]="isLoading"
  (nzOnCancel)="createModalVisible = false"
  (nzOnOk)="startCreate()">
  <ng-container *nzModalContent>
    <nz-radio-group [(ngModel)]="createMode" class="create-mode-picker">
      <label nz-radio nzValue="blank">
        <span class="choice-copy">
          <strong>Start blank</strong>
          <span>Use safe defaults and configure each section.</span>
        </span>
      </label>
      <label nz-radio nzValue="clone">
        <span class="choice-copy">
          <strong>Copy an existing fleet</strong>
          <span>Choose which safe sections to use as a starting point.</span>
        </span>
      </label>
    </nz-radio-group>
    <div *ngIf="createMode === 'clone'" class="clone-options">
      <div class="form-field">
        <span>Fleet to copy</span>
        <nz-select nzShowSearch aria-label="Fleet to copy" [(ngModel)]="cloneFleetId" nzPlaceHolder="Select fleet">
          <nz-option *ngFor="let fleet of fleets" [nzValue]="fleet.specialPriceSetId" [nzLabel]="fleet.name"></nz-option>
        </nz-select>
      </div>
      <div class="clone-sections">
        <label nz-checkbox [(ngModel)]="cloneSections.pricing">Pricing rules</label>
        <label nz-checkbox [(ngModel)]="cloneSections.skuExceptions">Stock code exceptions</label>
        <label nz-checkbox [(ngModel)]="cloneSections.availability">Availability and merchants</label>
        <label nz-checkbox [(ngModel)]="cloneSections.authFields">Authorisation fields</label>
        <label nz-checkbox [(ngModel)]="cloneSections.document">Procedure document</label>
      </div>
    </div>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="editFieldModalVisible"
  nzTitle="Edit authorisation field"
  nzOkText="Update field everywhere"
  [nzOkLoading]="isUpdatingAuthField"
  [nzOkDisabled]="!!authFieldUpdateNameError"
  [nzClosable]="!isUpdatingAuthField"
  [nzMaskClosable]="!isUpdatingAuthField"
  (nzOnCancel)="cancelEditAuthField()"
  (nzOnOk)="updateExistingAuthField()">
  <ng-container *nzModalContent>
    <div class="modal-form">
      <nz-alert
        nzType="warning"
        nzShowIcon
        nzMessage="This is a shared field"
        nzDescription="Updating its name or guidance changes it for every fleet that uses this field.">
      </nz-alert>
      <label class="form-field">
        <span>Field name <em>*</em></span>
        <input nz-input name="editAuthFieldName" autocomplete="off" maxlength="50" [(ngModel)]="authFieldUpdate.fleetAuthFieldName" [attr.aria-invalid]="!!authFieldUpdateNameError" aria-describedby="edit-auth-field-name-error">
        <small id="edit-auth-field-name-error" *ngIf="authFieldUpdateNameError" class="field-error" role="alert">{{ authFieldUpdateNameError }}</small>
      </label>
      <label class="form-field">
        <span>Help text</span>
        <textarea nz-input name="editAuthFieldHelp" autocomplete="off" rows="3" maxlength="250" [(ngModel)]="authFieldUpdate.tooltipText" placeholder="Explain what the branch must provide&hellip;"></textarea>
      </label>
      <div class="protected-field-details" *ngIf="editingAuthField">
        <div>
          <span>Input type</span>
          <strong>{{ authDataTypeLabel(editingAuthField.dataType) }}</strong>
        </div>
        <div *ngIf="editingAuthField.modelField">
          <span>StockFinder mapping</span>
          <strong translate="no">{{ editingAuthField.modelField }}</strong>
        </div>
        <small>Input type and StockFinder mapping are protected to keep existing responses and integrations compatible.</small>
      </div>
    </div>
  </ng-container>
</nz-modal>

<nz-modal
  [(nzVisible)]="newFieldModalVisible"
  nzTitle="New authorisation field"
  nzOkText="Add field"
  (nzOnCancel)="newFieldModalVisible = false"
  (nzOnOk)="addNewAuthField()">
  <ng-container *nzModalContent>
    <div class="modal-form">
      <label class="form-field">
        <span>Field name <em>*</em></span>
        <input nz-input name="newAuthFieldName" autocomplete="off" maxlength="50" [(ngModel)]="newField.fleetAuthFieldName" placeholder="e.g. Driver licence number">
      </label>
      <div class="form-field">
        <span>Input type</span>
        <nz-select aria-label="Authorisation field input type" [(ngModel)]="newField.dataType">
          <nz-option [nzValue]="1" nzLabel="Text"></nz-option>
          <nz-option [nzValue]="2" nzLabel="Number"></nz-option>
          <nz-option [nzValue]="3" nzLabel="Single file"></nz-option>
          <nz-option [nzValue]="4" nzLabel="Multiple files"></nz-option>
        </nz-select>
      </div>
      <label class="form-field">
        <span>Help text</span>
        <textarea nz-input name="newAuthFieldHelp" autocomplete="off" rows="3" maxlength="250" [(ngModel)]="newField.tooltipText" placeholder="Explain what the branch must provide…"></textarea>
      </label>
      <label nz-checkbox [(ngModel)]="newField.isRequired">Required before authorisation</label>
    </div>
  </ng-container>
</nz-modal>
